/**
 * Shared country list used by country-selecting form fields across the app.
 *
 * `code` is the ISO 3166-1 alpha-2 code; `name` is the human-readable
 * English name. Address-lookup providers return country *names* (e.g.
 * "United Kingdom"), not codes - see
 * `backend/apps/integrations/providers/{goaddress,fetchify,portalgo,smartystreets}.py`.
 * Org/tenant preferences store ISO codes. Each consumer picks the
 * representation that matches its persisted shape; the canonical list
 * lives here so both stay in sync.
 */
export interface Country {
  code: string
  name: string
}

export const COUNTRIES: Country[] = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IE', name: 'Ireland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'IN', name: 'India' },
  { code: 'JP', name: 'Japan' },
]

/**
 * Country options keyed by name (value === label === country name).
 *
 * Use for address-style fields whose persisted value is a country
 * *name*, e.g. `address_country` populated by address autocomplete.
 */
export const COUNTRY_NAME_OPTIONS: { value: string; label: string }[] =
  COUNTRIES.map((c) => ({ value: c.name, label: c.name }))

/**
 * Country options keyed by ISO code (value === code, label === name).
 *
 * Use for fields that persist an ISO code, e.g. organisation
 * `default_country_code`.
 */
export const COUNTRY_CODE_OPTIONS: { value: string; label: string }[] =
  COUNTRIES.map((c) => ({ value: c.code, label: c.name }))

/** ISO 3166-1 alpha-2 code keyed by lower-cased country name. */
const COUNTRY_CODE_BY_NAME: Record<string, string> = COUNTRIES.reduce(
  (acc, c) => {
    acc[c.name.toLowerCase()] = c.code
    return acc
  },
  {} as Record<string, string>,
)

/** Country name keyed by lower-cased ISO 3166-1 alpha-2 code. */
const COUNTRY_NAME_BY_CODE: Record<string, string> = COUNTRIES.reduce(
  (acc, c) => {
    acc[c.code.toLowerCase()] = c.name
    return acc
  },
  {} as Record<string, string>,
)

/**
 * Map a full country *name* to its ISO 3166-1 alpha-2 *code*.
 *
 * Address-lookup providers return country names ("United States"), but some
 * fields persist an ISO code (e.g. `AddressValue.countryCode` used by
 * `AddressInput`). Comparing a name against a code always mismatches, and
 * storing a name where a code is expected breaks the country `<Select>`, so
 * name-keyed results must be normalised through this helper before they are
 * compared with or written back into a code-keyed field. Unknown names fall
 * through unchanged (already an ISO code, or a value we do not recognise).
 */
export function countryNameToCode(name: string): string {
  const trimmed = name.trim()
  return COUNTRY_CODE_BY_NAME[trimmed.toLowerCase()] ?? trimmed
}

/**
 * Map an ISO 3166-1 alpha-2 *code* to its full country *name*.
 *
 * The inverse of {@link countryNameToCode}. Name-keyed surfaces (the shared
 * `AddressCapture` country `<Select>` uses `COUNTRY_NAME_OPTIONS`) need a name,
 * but config can legitimately carry an ISO code (an attribute's
 * `configuration_data.default_country`, org preferences). Feeding a raw code
 * into the name-keyed Select leaves it blank and makes name-vs-code guards
 * short-circuit, silently breaking postcode autofill. Normalise a possible code
 * through this helper first. Unknown values fall through unchanged (already a
 * name, or a value we do not recognise).
 */
export function countryCodeToName(code: string): string {
  const trimmed = code.trim()
  return COUNTRY_NAME_BY_CODE[trimmed.toLowerCase()] ?? trimmed
}

/**
 * Heuristic: does this field represent a country?
 *
 * Recognises the standard `code_name` / `maps_to_column` conventions
 * used throughout the address pipeline:
 *
 * - `code_name`         → `country` | `address_country`
 * - `maps_to_column`    → `country` | `address_country`
 *
 * Tenant-defined attributes that legitimately want a free-text country
 * value should pick a different `code_name`.
 */
export function isCountryField(field: {
  code_name?: string
  maps_to_column?: string
}): boolean {
  const cn = field.code_name?.toLowerCase()
  const mtc = field.maps_to_column?.toLowerCase()
  return (
    cn === 'country' ||
    cn === 'address_country' ||
    mtc === 'country' ||
    mtc === 'address_country'
  )
}
